import { useEffect, useRef } from "react";
import type { AIActionApprovalResponse } from "@k8s-dashboard/contracts";
import { useT } from "@/i18n/I18nProvider";
import { APPROVAL_ERROR_MESSAGE, CHANGE_FIELD_MESSAGE, FAILURE_MESSAGE, OUTCOME_MESSAGE, retryable } from "./approvalLabels";
import type { ApprovalState } from "./approvalState";

/**
 * 승인 확인 창 — ADR 0028 Phase C
 * --------------------------------------------------------------------------
 * 여기 있는 값은 **전부 서버가 만든 것**이고, 라벨은 전부 화면이 고른 것입니다.
 * 자유 문자열이 그려지는 자리가 없으므로 모델·클러스터 문구가 화면 문장이 되지 않습니다.
 *
 * 접근성 규칙 셋입니다.
 *
 *   포커스   열면 제목으로 갑니다. 오류가 나면 오류 문단으로 옮겨, 스크린리더
 *           사용자가 무엇이 잘못됐는지 먼저 듣습니다.
 *   Escape  진행 중이 아닐 때만 닫힙니다 — 제출 중 창을 닫아도 결과는 남습니다.
 *   live 없음 이 창에는 live region이 없습니다. 낭독은 공용 Announcer 하나가 합니다.
 */
export function ApprovalDialog({
  titleId,
  state,
  review,
  onConfirm,
  onRetry,
  onClose,
}: {
  titleId: string;
  state: ApprovalState;
  review?: AIActionApprovalResponse;
  onConfirm: () => void;
  onRetry: () => void;
  onClose: () => void;
}) {
  const { t } = useT("approve");
  const titleRef = useRef<HTMLHeadingElement>(null);
  const errorRef = useRef<HTMLParagraphElement>(null);
  const failing = state.kind === "error";

  useEffect(() => {
    // 오류가 있으면 오류로, 없으면 제목으로. 두 경우 모두 결정적입니다.
    (failing ? errorRef.current : titleRef.current)?.focus();
  }, [failing]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const busy = state.kind === "confirming";
  const settled = state.kind === "settled" ? state.response : undefined;
  return (
    <div className="ds-confirm-overlay" role="presentation" onClick={onClose}>
      <div
        className="ds-confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="agent-approval-dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id={titleId} ref={titleRef} tabIndex={-1} className="ds-confirm-dialog__title">
          {t("approve.title")}
        </h2>
        {failing ? (
          <p ref={errorRef} tabIndex={-1} className="ds-confirm-dialog__message" data-testid="agent-approval-error">
            {t(APPROVAL_ERROR_MESSAGE[state.code])}
          </p>
        ) : settled ? (
          <Result response={settled} />
        ) : (
          <Review response={review} />
        )}
        <div className="ds-confirm-dialog__actions">
          <button type="button" className="linkish" onClick={onClose} disabled={busy} data-testid="agent-approval-close">
            {settled ? t("approve.close") : t("approve.cancel")}
          </button>
          {failing && retryable(state.code) && (
            <button type="button" className="btn" onClick={onRetry} data-testid="agent-approval-retry">
              {t("approve.retry")}
            </button>
          )}
          {!failing && !settled && (
            <button
              type="button"
              className="btn btn--critical"
              onClick={onConfirm}
              disabled={busy || !review?.review}
              aria-busy={busy}
              data-testid="agent-approval-confirm"
            >
              {busy ? t("approve.confirming") : t("approve.confirm")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** 검토 요약. 필드는 닫힌 열거이고 값은 계약이 검증한 정수·quantity뿐입니다. */
function Review({ response }: { response?: AIActionApprovalResponse }) {
  const { t } = useT("approve");
  if (!response?.review) return <p className="ds-confirm-dialog__message">{t("approve.reviewing")}</p>;
  const { changes, expiresAt } = response.review;
  return (
    <div className="ds-confirm-dialog__message">
      <p data-testid="agent-approval-intro">{t("approve.intro")}</p>
      <dl className="ds-approval__changes" aria-label={t("approve.summary")} data-testid="agent-approval-summary">
        {changes.map((change) => (
          <div key={change.field}>
            <dt>{t(CHANGE_FIELD_MESSAGE[change.field])}</dt>
            {/* 텍스트로만 그립니다 — HTML도 Markdown도 해석하지 않습니다. */}
            <dd>
              {change.from === undefined
                ? t("approve.change.set", { to: change.to })
                : t("approve.change.move", { from: change.from, to: change.to })}
            </dd>
          </div>
        ))}
      </dl>
      <p data-testid="agent-approval-expires">{t("approve.expires", { time: expiresAt })}</p>
    </div>
  );
}

/** 종단 결과. UNKNOWN이 별도 문구인 것이 핵심입니다 — 실패와 다릅니다. */
function Result({ response }: { response: AIActionApprovalResponse }) {
  const { t } = useT("approve");
  const outcome = response.outcome ?? (response.status === "EXECUTED" ? "EXECUTED" : "FAILED");
  return (
    <div className="ds-confirm-dialog__message" data-testid="agent-approval-result">
      <p>{t(OUTCOME_MESSAGE[outcome])}</p>
      {response.failureCode && (
        <p data-testid="agent-approval-failure">{t(FAILURE_MESSAGE[response.failureCode])}</p>
      )}
    </div>
  );
}
